import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import config from "../../config.js";
import "./addUser.css";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  CircularProgress,
  useTheme,
  IconButton,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import StarIcon from "../../components/starIcon/StarIcon";
import TitleBar from "../../components/navbar/TitleBar";
import NavMenu from "../../components/navbar/NavMenu";
import { useTranslation } from "react-i18next";
import SubTitle from "../../components/common/SubTitle";
import ImageAdd from "../../images/drivers/Driver_icon.svg";
import { Skeleton } from "@mui/material";
import Edit_Icon from "../../images/table/Edit_Icon.svg";
import { usePermission } from "../../hooks/usePermission";
import { useUser } from "../login/UserContextPersistent";
import Footer from "../../components/common/Footer.jsx";
import SchoolBusNavMenu from "../../components/createForm/SchoolBusNavMenu.jsx";

const AddUser = ({ embedded = false, embeddedUserId, onCancel, onComplete }) => {
  const location = useLocation();
  const { dispatch } = useUser();
  const id = embedded ? embeddedUserId : location.state?.id;
  const Navigate = useNavigate();
  const { userId } = useParams();
  const isSchoolApp =
    localStorage.getItem("selectedApp") === "schoolTrack" ||
    location.pathname.startsWith("/school-users");
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const [isLoading, setIsLoading] = useState(!!id);
  const [allRoles, setAllRoles] = useState([]);
  const [errors, setErrors] = useState({
    fullName: "",
    email: "",
    userName: "",
    password: "",
    confirmPassword: "",
    roleId: "",
  });
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] =
    useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [pass, setPass] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [err, setErr] = useState();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    userName: "",
    password: "",
    confirmPassword: "",
    roleId: "",
    department: "",
    status: "ACTIVE",
  });

  const [labels, setLabels] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [parents, setParents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedReference, setSelectedReference] = useState("");

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const [imageUploadError, setImageUploadError] = useState("");

  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;
  const isProfilePage = location.state?.from === "profile";

  const title = isProfilePage
    ? t("Hello {{name}}", {
        name:
          JSON.parse(localStorage.getItem("userState"))?.user?.name?.split(
            " ",
          )[0] || t("User"),
      })
    : id || userId
      ? "Update User"
      : "Create User";

  const path = isProfilePage
    ? `${orgName || "organization"} > My Profile`
    : id || userId
      ? `${orgName || "organization"} > Users > ${
          formData?.fullName || "selected user"
        } `
      : `${orgName || "organization"} > Users > Create`;

  // Permission constants
  const { hasPermission } = usePermission();
  const canCreateUser = hasPermission("/api/user", "POST"); // Remove trailing slash
  const canModifyRole = hasPermission("/api/user/role", "PATCH"); // Keep as is
  const canResetPassword = hasPermission("/api/user/password", "PATCH"); // Keep as is

  const isAdmin =
    JSON.parse(localStorage.getItem("userState"))?.user?.role === "Admin";

  // Fetch user data and roles
  const handleGetUserAndOrganization = (id) => {
    let isMounted = true;
    if (id) {
      setIsLoading(true);
      axios
        .get(`${config.baseURL}/user/${id}`, { withCredentials: true })
        .then(async (response) => {
          if (isMounted && response && response.data.user) {
            const user = response.data.user;
            setFormData({
              fullName: user.name,
              email: user.email,
              userName: user.userName,
              roleId: user.roleId || user.roles?.id || "",
              password: "",
              confirmPassword: "",
              department: user.department || "",
              status:
                String(user.status || "ACTIVE").toUpperCase() === "INACTIVE"
                  ? "INACTIVE"
                  : "ACTIVE",
            });
            if (user.labels && user.labels.length > 0) {
              const userLabel = user.labels[0];
              setSelectedLabel(userLabel);

              const assignment = user.labelAssignments?.find(
                (a) => a.labelId === userLabel.id,
              );
              if (assignment?.referenceId) {
                setSelectedReference(assignment.referenceId); // sets the driver dropdown
              }
            }
            if (user.Document && Array.isArray(user.Document)) {
              const profileDoc = user.Document.find(
                (doc) => doc.name === "profile" && doc.location,
              );
              if (profileDoc) {
                const imageUrl = `${config.baseURL}/upload/image?fileName=${profileDoc.location}`;
                const imageBlobUrl = await fetchImage(imageUrl);
                setImagePreview(imageBlobUrl);
              }
            }
          }
        })
        .catch((error) => console.error("Error fetching user data:", error))
        .finally(() => setIsLoading(false));
    }

    axios
      .get(`${config.baseURL}/role/organization`, { withCredentials: true })
      .then((res) => {
        if (isMounted) setAllRoles(res.data.roles);
      })
      .catch((error) => {
        console.log("Error in fetching roles", error);
      });

    return () => {
      isMounted = false;
    };
  };

  const getLabels = async () => {
    try {
      const response = await axios.get(`${config.baseURL}/label`, {
        withCredentials: true,
      });
      setLabels(
        Array.isArray(response.data)
          ? response.data
          : (response.data?.labels ?? response.data?.data ?? []),
      );
    } catch (error) {
      console.error("Error fetching labels:", error);
      return [];
    }
  };

  const getAllDrivers = async () => {
    try {
      const parsedData = JSON.parse(localStorage.getItem("userState"));
      const orgId = parsedData?.user?.organizationId;

      const response = await axios.get(
        `${config.baseURL}/driver/organization`,
        {
          params: { organizationId: orgId },
          withCredentials: true,
        },
      );
      setDrivers(response.data || []);
    } catch (error) {
      console.error("Error while fetching the data: ", error);
      throw error;
    }
  };

  const getAllParents = async () => {
    try {
      const parsedData = JSON.parse(localStorage.getItem("userState"));
      const orgId = parsedData?.user?.organizationId;

      const response = await axios.get(
        `${config.baseURL}/parent/organization`,
        {
          params: { organizationId: orgId },
          withCredentials: true,
        },
      );
      setParents(response.data || []);
    } catch (error) {
      console.error("Error while fetching parents: ", error);
      throw error;
    }
  };

  const getAllEmployees = async () => {
    try {
      const parsedData = JSON.parse(localStorage.getItem("userState"));
      const orgId = parsedData?.user?.organizationId;

      const response = await axios.get(
        `${config.baseURL}/employee/organization`,
        {
          params: { organizationId: orgId },
          withCredentials: true,
        },
      );
      setEmployees(response.data || []);
    } catch (error) {
      console.error("Error while fetching employees: ", error);
      throw error;
    }
  };

  const assignLabelToUser = async (userId) => {
    if (!selectedLabel) return;

    const body = {
      entityType: "users",
      entityId: userId,
    };

    if (selectedReference) {
      // Generic linkage for USER labels (driver/parent/etc.)
      body.referenceId = selectedReference;
    }

    // Keep backward compatibility with existing backend clients/flows.
    if (selectedLabel.type === "DRIVER" && selectedReference) {
      body.driverId = selectedReference;
    }
    if (selectedLabel.type === "PARENT" && selectedReference) {
      body.parentId = selectedReference;
    }
    if (selectedLabel.type === "EMPLOYEE" && selectedReference) {
      body.employeeId = selectedReference;
    }

    try {
      await axios.post(
        `${config.baseURL}/label/${selectedLabel.id}/assign`,
        body,
        { withCredentials: true },
      );
    } catch (error) {
      console.error("Error assigning label to user:", error);
    }
  };

  useEffect(() => {
    handleGetUserAndOrganization(id);
    getLabels();
    getAllDrivers();
    getAllParents();
    getAllEmployees();
  }, [id]);

  const fetchImage = async (imageUrl) => {
    try {
      const response = await axios.get(imageUrl, {
        responseType: "arraybuffer",
        withCredentials: true,
      });
      const blob = new Blob([response.data], { type: "image/jpeg" });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error("Error fetching image:", error);
      return null;
    }
  };

  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadProfileImage = async (userId) => {
    if (!profileImage) return; // No image to upload
    const formData = new FormData();
    formData.append("image", profileImage);

    try {
      const response = await axios.post(
        `${config.baseURL}/upload/image?name=profile&associateEntityId=${userId}&associatedWith=User`,
        formData,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
    } catch (error) {
      console.error("Error uploading image:", error);
      setImageUploadError(t("Failed to upload profile image. Please try again."));
    }
  };

  // Event Handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handlePassChange = (e) => {
    const { name, value } = e.target;
    setPass((prev) => ({ ...prev, [name]: value }));
    setPasswordError("");
  };

  const handleRoleChange = (e) => {
    const selectedRoleName = e.target.value;
    const selectedRole = allRoles.find(
      (role) => role.name === selectedRoleName,
    );

    if (selectedRole) {
      setFormData((prev) => ({
        ...prev,
        roleId: selectedRole.id,
      }));
      setErrors((prev) => ({ ...prev, roleId: "" }));
    }
  };

  // Modal handlers
  const handleOpenResetPasswordModal = () => {
    setIsResetPasswordModalOpen(true);
    setPasswordError("");
  };

  const handleResetPasswordCancel = () => {
    setIsResetPasswordModalOpen(false);
    setPass({
      newPassword: "",
      confirmPassword: "",
    });
    setPasswordError("");
  };

  const validatePassword = (password) => {
    if (!password) {
      return t("Password is required");
    }
    if (password.length < 6) {
      return t("Password must be at least 6 characters long");
    }
    if (!/[A-Z]/.test(password)) {
      return t("Password must contain at least one uppercase letter (A-Z)");
    }
    if (!/[a-z]/.test(password)) {
      return t("Password must contain at least one lowercase letter (a-z)");
    }
    if (!/[0-9]/.test(password)) {
      return t("Password must contain at least one number (0-9)");
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
      return t("Password must contain at least one special character (e.g. @, #, $, !)");
    }
    return "";
  };

  const handleResetPasswordConfirm = async () => {
    if (!canResetPassword) {
      setPasswordError(t("You don't have permission to reset passwords"));
      return;
    }

    if (!pass.newPassword || !pass.confirmPassword) {
      setPasswordError(t("Both fields are required"));
      return;
    }

    const passwordValidationErr = validatePassword(pass.newPassword);
    if (passwordValidationErr) {
      setPasswordError(passwordValidationErr);
      return;
    }

    if (pass.newPassword !== pass.confirmPassword) {
      setPasswordError(t("Passwords do not match"));
      return;
    }
    setIsResettingPassword(true);
    try {
      await axios.patch(
        `${config.baseURL}/user/password`,
        {
          userId: id,
          newPassword: pass.confirmPassword,
        },
        { withCredentials: true },
      );
      setIsResetPasswordModalOpen(false);
      setPass({
        newPassword: "",
        confirmPassword: "",
      });
      setPasswordError("");
    } catch (error) {
      console.error("Error resetting password:", error);
      setPasswordError(t("Failed to reset password. Please try again."));
    } finally {
      setIsResettingPassword(false);
    }
  };

  const validateFullName = (name) => {
    const nameParts = name.trim().split(" ");
    return nameParts.length >= 2 && nameParts[0] && nameParts[1];
  };
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {
      fullName: "",
      email: "",
      userName: "",
      password: "",
      confirmPassword: "",
      // roleId: "",
    };
    let isValid = true;

    if (!validateFullName(formData.fullName)) {
      newErrors.fullName = t("Please enter both first name and last name");
      isValid = false;
    }
    if (!formData.email) {
      newErrors.email = t("Email is required");
      isValid = false;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t("Please enter a valid email address");
      isValid = false;
    }
    if (!formData.userName) {
      newErrors.userName = t("Username is required");
      isValid = false;
    }
    if (!id) {
      const passwordErr = validatePassword(formData.password);
      if (passwordErr) {
        newErrors.password = passwordErr;
        isValid = false;
      }
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = t("Confirm password is required");
        isValid = false;
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = t("Passwords do not match");
        isValid = false;
      }
    }
    // if (!formData.roleId) {
    //   newErrors.roleId = "Role is required";
    //   isValid = false;
    // }

    setErrors(newErrors);
    return isValid;
  };
  function updateUserProfileInLocalStorage({ name, email, role, roleId }) {
    const userState = JSON.parse(localStorage.getItem("userState"));

    if (!userState || typeof userState !== "object") return;

    userState.user = {
      ...userState.user,
      ...(name && { name }),
      ...(email && { email }),
      ...(role && { role }),
      ...(roleId && { roleId }), // if you track roleId too
    };

    localStorage.setItem("userState", JSON.stringify(userState));
  }
  const submitHandler = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErr(null);

    try {
      let userId;
      if (id) {
        // Update existing user - separate role update from name/email update
        const updateData = {
          userId: id,
          name: formData.fullName.trim(),
          email: formData.email,
          department: formData.department.trim() || null,
        };

        if (!isProfilePage) {
          updateData.status = formData.status === "INACTIVE" ? "INACTIVE" : "ACTIVE";
        }

        // Only include roleId if user has permission to modify roles
        if (canModifyRole && formData?.roleId) {
          updateData.roleId = formData.roleId;
        }

        await axios.put(`${config.baseURL}/user`, updateData, {
          withCredentials: true,
        });
        userId = id;

        const signedInUserId = JSON.parse(localStorage.getItem("userState"))?.user?.id;
        if (String(signedInUserId) === String(id)) {
          const roleName = allRoles.find((role) => role?.id === formData?.roleId);
          dispatch({
            type: "UPDATE_USER",
            payload: {
              name: formData.fullName.trim(),
              email: formData.email,
              role: roleName?.name,
              roleId: formData.roleId,
            },
          });
          updateUserProfileInLocalStorage({
            name: formData.fullName.trim(),
            email: formData.email,
            role: roleName?.name,
            roleId: formData.roleId,
          });
        }
      } else {
        // Create new user - no changes needed here
        const response = await axios.post(
          `${config.baseURL}/user`,
          {
            name: formData.fullName.trim(),
            email: formData.email,
            password: formData.password,
            userName: formData.userName,
            organizationId: JSON.parse(localStorage.getItem("userState")).user
              .organizationId,
            roleId: formData.roleId || null,
            department: formData.department.trim() || null,
            status: formData.status === "INACTIVE" ? "INACTIVE" : "ACTIVE",
          },
          { withCredentials: true },
        );
        userId = response.data.user.id;
        const invite = response.data?.invite;
        if (invite?.sent) {
          toast.success(t("loginInviteSent", { email: formData.email }));
        } else if (invite && invite.sent === false) {
          toast.warning(t("loginInviteFailed"));
        }
      }

      await uploadProfileImage(userId);
      await assignLabelToUser(userId);

      if (embedded) onComplete?.();
      else Navigate(-1);
    } catch (error) {
      setErr(error.response?.data?.error || error.message);
      console.error("Error in user operation:", error);
      setErrors((prev) => ({
        ...prev,
        general: t("Failed to save user. Please try again."),
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (embedded) onCancel?.();
    else Navigate(-1);
  };

  const findRoleNameById = (roleId) => {
    const role = allRoles?.find((role) => role.id === roleId);
    return role ? role.name : "";
  };

  // Styles
  const textFieldProps = {
    fullWidth: true,
    variant: "outlined",
    InputProps: {
      style: {
        borderRadius: "16px",
      },
    },
    sx: {
      "& .MuiInputBase-input": {
        color: "#111214",
        fontFamily: "Roboto",
        fontWeight: 500,
        fontStyle: "medium",
        fontSize: "20px",
      },
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderWidth: "2px",
          borderColor: "rgba(24, 48, 89, 1)",
        },
        "&.Mui-error fieldset": {
          borderColor: "rgba(24, 48, 89, 1)",
        },
        "&.Mui-disabled": {
          backgroundColor: "#e9ecef",
          "& fieldset": {
            borderColor: "transparent",
          },
        },
      },
    },
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: embedded ? "auto" : "100vh",
      }}
    >
      <Box>
        {!embedded && <TitleBar />}
        {!embedded && (isSchoolApp ? (
          <SchoolBusNavMenu />
        ) : (
          <NavMenu />
        ))}
        {!embedded && <SubTitle title={title} path={path} />}
        {isLoading ? (
          <div
            style={{
              padding: "15px 30px",
              borderRadius: "10px",
              border: "1px solid #A5DCF3",
              margin: embedded ? 0 : "10px 40px",
              boxShadow: "-2px 2px 5px 0px #00000040",
            }}
          >
            <Skeleton variant="rectangular" height={52} sx={{ mb: 2 }} />
            <div style={{ display: "flex", gap: "12px", marginBottom: 16 }}>
              <Skeleton variant="circular" width={100} height={100} />
              <Skeleton variant="rectangular" width={156} height={40} />
            </div>
            <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
            <Skeleton
              variant="rectangular"
              height={48}
              width={120}
              sx={{ mt: 2 }}
            />
            <Skeleton
              variant="rectangular"
              height={48}
              width={120}
              sx={{ mt: 2, ml: 2 }}
            />
          </div>
        ) : (
          <div
            style={{
              padding: "15px 30px",
              borderRadius: "10px",
              border: "1px solid #A5DCF3",
              margin: embedded ? 0 : "10px 40px",
              boxShadow: "-2px 2px 5px 0px #00000040",
            }}
          >
            <Typography
              style={{
                height: "52px",
                borderRadius: "8px",
                gap: "16px",
                padding: "12px 24px 8px 24px",
                background: "#647490",
                boxShadow: "-2px 2px 5px 0px #00000040",
                color: "white",
                fontSize: "20px",
              }}
            >
              {isProfilePage ? t("My Profile") : t("User Details")}
            </Typography>

            <div
              style={{
                marginTop: "16px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{
                  width: "100px",
                  height: "100px",
                  borderRadius: "50%",
                  border: imagePreview ? "1px solid #201E46" : "none",
                  backgroundColor: imagePreview ? "transparent" : "#f8f9fa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  // overflow: "hidden",
                  position: "relative",
                  cursor: "pointer",
                }}
              >
                {imagePreview ? (
                  <>
                    <img
                      src={imagePreview}
                      alt="User Avatar"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "50%",
                      }}
                    />
                    {/* Edit icon overlay */}
                    <div
                      style={{
                        position: "absolute",
                        top: "0px",
                        right: "0px",
                        width: "24px",
                        height: "24px",
                        borderRadius: "50%",
                        backgroundColor: "#FFFFFF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                      }}
                    >
                      <img src={Edit_Icon} alt="edit" />
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#20A8E0",
                    }}
                  >
                    <img alt="profile" src={ImageAdd} />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    opacity: 0,
                    cursor: "pointer",
                  }}
                />
              </div>
              {imageUploadError && (
                <p style={{ color: "red", marginTop: "8px" }}>
                  {imageUploadError}
                </p>
              )}
              {err && <p style={{ color: "red", marginTop: "8px" }}>{err}</p>}
              {id && canResetPassword && (
                <button
                  style={{
                    border: "2px solid #20A8E0",
                    color: "#20A8E0",
                    width: "156px",
                    height: "40px",
                    borderRadius: "8px",
                    padding: "10px 20px",
                    background: "white",
                    cursor: "pointer",
                  }}
                  onClick={handleOpenResetPasswordModal}
                >
                  {t("Reset Password")}
                </button>
              )}
            </div>

            <Box
              component="form"
              autoComplete="off"
              style={{ marginTop: "20px" }}
            >
              <Grid container spacing={"20px"}>
                <Grid item xs={12} sm={4}>
                  <InputLabel
                    className="input-label"
                    // sx={{
                    //   marginBottom: "10px",
                    //   fontFamily: "Roboto",
                    //   fontStyle: "SemiBold",
                    //   fontSize: "20px",
                    //   color: "#1F242E",
                    // }}
                  >
                    {t("Full Name")} <StarIcon />
                  </InputLabel>
                  <TextField
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder={`${t("Type First Name Last Name")} (e.g. John Doe)`}
                    error={!!errors.fullName}
                    helperText={errors.fullName}
                    {...textFieldProps}
                    inputProps={{
                      autocomplete: "new-fullname",
                      form: { autocomplete: "off" },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel className="input-label">
                    {t("email")} <StarIcon />
                  </InputLabel>
                  <TextField
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={`${t("Type Email Address")} (e.g.john@example.com)`}
                    error={!!errors.email}
                    helperText={errors.email}
                    {...textFieldProps}
                    inputProps={{
                      autocomplete: "new-email",
                      form: {
                        autocomplete: "off",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel className="input-label">
                    {t("username")} <StarIcon />
                  </InputLabel>
                  <TextField
                    name="userName"
                    value={formData.userName}
                    onChange={handleChange}
                    placeholder={`${t("Type Username")} (e.g.johndoe123)`}
                    error={!!errors.userName}
                    helperText={errors.userName}
                    disabled={!!id}
                    {...textFieldProps}
                    inputProps={{
                      autocomplete: "new-username",
                      form: {
                        autocomplete: "off",
                      },
                    }}
                  />
                </Grid>

                {!id && (
                  <>
                    <Grid item xs={12} sm={4}>
                      <InputLabel className="input-label">
                        {t("password")} <StarIcon />
                      </InputLabel>
                      <TextField
                        className="password-toggle-field"
                        name="password"
                        type={showNewPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={handleChange}
                        placeholder={t("Type Password")}
                        error={!!errors.password}
                        helperText={errors.password}
                        {...textFieldProps}
                        InputProps={{
                          ...textFieldProps.InputProps,
                          endAdornment: (
                            <IconButton
                              aria-label={showNewPassword ? t("Hide password") : t("Show password")}
                              onClick={() => setShowNewPassword((current) => !current)}
                              edge="end"
                            >
                              {showNewPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          ),
                        }}
                        inputProps={{
                          autocomplete: "new-password",
                          form: {
                            autocomplete: "off",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <InputLabel className="input-label">
                        {t("Confirm Password")} <StarIcon />
                      </InputLabel>
                      <TextField
                        className="password-toggle-field"
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        placeholder={t("Confirm Password")}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword}
                        {...textFieldProps}
                        InputProps={{
                          ...textFieldProps.InputProps,
                          endAdornment: (
                            <IconButton
                              aria-label={
                                showConfirmPassword
                                  ? t("Hide password")
                                  : t("Show password")
                              }
                              onClick={() => setShowConfirmPassword((current) => !current)}
                              edge="end"
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          ),
                        }}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12} sm={4}>
                  <InputLabel className="input-label">
                    {/* {t("role")} <StarIcon /> */}
                    {t("role")}
                  </InputLabel>
                  <FormControl fullWidth error={!!errors.roleId}>
                    <Select
                      value={findRoleNameById(formData.roleId) || ""}
                      onChange={handleRoleChange}
                      displayEmpty
                      disabled={!canModifyRole} // Add this line
                      sx={{
                        borderRadius: "16px",
                        height: "56px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "2px solid rgba(24, 48, 89, 1)",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#e9ecef",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#ccc",
                          },
                        },
                        "& .MuiSelect-select": {
                          padding: "14px",
                        },
                        "& .MuiMenu-paper": {
                          maxHeight: "200px",
                          marginTop: "8px",
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        {t("Select Role")}
                      </MenuItem>
                      {allRoles?.map((role) => (
                        <MenuItem key={role.id} value={role.name}>
                          {role.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.roleId && (
                      <p
                        style={{
                          color: "#d32f2f",
                          fontSize: "12px",
                          marginTop: "4px",
                        }}
                      >
                        {errors.roleId}
                      </p>
                    )}
                    {!canModifyRole && (
                      <p
                        style={{
                          color: "#666",
                          fontSize: "12px",
                          marginTop: "4px",
                        }}
                      >
                        {/* {t("You don't have permission to modify roles")} */}
                      </p>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel className="input-label">
                    {t("Department")}
                  </InputLabel>
                  <TextField
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    placeholder={t("Type Department")}
                    {...textFieldProps}
                    inputProps={{
                      autocomplete: "off",
                    }}
                  />
                </Grid>
                {!isProfilePage && (
                  <Grid item xs={12} sm={4}>
                    <InputLabel className="input-label">
                      {t("Status")}
                    </InputLabel>
                    <FormControl fullWidth>
                      <Select
                        name="status"
                        value={formData.status || "ACTIVE"}
                        onChange={handleChange}
                        sx={{
                          borderRadius: "16px",
                          height: "56px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "2px solid rgba(24, 48, 89, 1)",
                          },
                          "& .MuiSelect-select": {
                            padding: "14px",
                          },
                        }}
                      >
                        <MenuItem value="ACTIVE">{t("Active")}</MenuItem>
                        <MenuItem value="INACTIVE">{t("Inactive")}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                {isAdmin && (
                  <>
                    <Grid item xs={12} sm={4}>
                      <InputLabel className="input-label">
                        {t("Label")}
                      </InputLabel>
                      <FormControl fullWidth error={!!errors.roleId}>
                        <Select
                          value={selectedLabel?.name || ""}
                          onChange={(e) => {
                            const selected = labels.find(
                              (label) => label.name === e.target.value,
                            );
                            setSelectedLabel(selected || "");
                            setSelectedReference("");
                          }}
                          displayEmpty
                          disabled={!canModifyRole}
                          sx={{
                            borderRadius: "16px",
                            height: "56px",
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "2px solid rgba(24, 48, 89, 1)",
                            },
                            "&.Mui-disabled": {
                              backgroundColor: "#e9ecef",
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#ccc",
                              },
                            },
                            "& .MuiSelect-select": {
                              padding: "14px",
                            },
                            "& .MuiMenu-paper": {
                              maxHeight: "200px",
                              marginTop: "8px",
                            },
                          }}
                        >
                          <MenuItem value="" disabled>
                            {t("Select Label")}
                          </MenuItem>
                          {labels?.map((label) => (
                            <MenuItem key={label.id} value={label.name}>
                              {label.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.roleId && (
                          <p
                            style={{
                              color: "#d32f2f",
                              fontSize: "12px",
                              marginTop: "4px",
                            }}
                          >
                            {errors.roleId}
                          </p>
                        )}
                        {!canModifyRole && (
                          <p
                            style={{
                              color: "#666",
                              fontSize: "12px",
                              marginTop: "4px",
                            }}
                          >
                            {/* {t("You don't have permission to modify roles")} */}
                          </p>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <InputLabel className="input-label">
                        {/* {t("role")} <StarIcon /> */}
                        {t("Reference ID")}
                      </InputLabel>
                      <FormControl fullWidth error={!!errors.roleId}>
                        <Select
                          value={selectedReference}
                          onChange={(e) => setSelectedReference(e.target.value)}
                          displayEmpty
                          disabled={
                            !canModifyRole ||
                            !["DRIVER", "PARENT", "EMPLOYEE"].includes(
                              selectedLabel?.type,
                            )
                          }
                          sx={{
                            borderRadius: "16px",
                            height: "56px",
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "2px solid rgba(24, 48, 89, 1)",
                            },
                            "&.Mui-disabled": {
                              backgroundColor: "#e9ecef",
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#ccc",
                              },
                            },
                            "& .MuiSelect-select": { padding: "14px" },
                            "& .MuiMenu-paper": {
                              maxHeight: "200px",
                              marginTop: "8px",
                            },
                          }}
                        >
                          <MenuItem value="" disabled>
                            {t("Select reference")}
                          </MenuItem>
                          {selectedLabel?.type === "DRIVER" &&
                            drivers?.map((driver) => (
                              <MenuItem key={driver.id} value={driver.id}>
                                {driver.first_name} {driver.last_name} -{" "}
                                {driver.driving_license_number}
                              </MenuItem>
                            ))}
                          {selectedLabel?.type === "PARENT" &&
                            parents?.map((parent) => (
                              <MenuItem key={parent.id} value={parent.id}>
                                {parent.firstName} {parent.lastName} -{" "}
                                {parent.phoneNumber}
                              </MenuItem>
                            ))}
                          {selectedLabel?.type === "EMPLOYEE" &&
                            employees?.map((employee) => (
                              <MenuItem key={employee.id} value={employee.id}>
                                {employee.firstName} {employee.lastName}
                                {employee.staffCode
                                  ? ` - ${employee.staffCode}`
                                  : ""}
                              </MenuItem>
                            ))}
                        </Select>
                        {errors.roleId && (
                          <p
                            style={{
                              color: "#d32f2f",
                              fontSize: "12px",
                              marginTop: "4px",
                            }}
                          >
                            {errors.roleId}
                          </p>
                        )}
                        {!canModifyRole && (
                          <p
                            style={{
                              color: "#666",
                              fontSize: "12px",
                              marginTop: "4px",
                            }}
                          >
                            {/* {t("You don't have permission to modify roles")} */}
                          </p>
                        )}
                      </FormControl>
                    </Grid>
                  </>
                )}
              </Grid>

              <Grid
                container
                spacing={3}
                style={{
                  marginTop: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Grid item xs={12} sm={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    style={{
                      fontWeight: 600,
                      fontSize: "18px",
                      height: "55px",
                      textTransform: "none",
                      borderRadius: "8px",
                      fontSize: "20px",
                      color: "#111214",
                      backgroundColor: "#F2F2F3",
                      border: "1px solid #414958",
                      opacity: isSubmitting ? 0.6 : 1,
                    }}
                  >
                    {t("Cancel")}
                  </Button>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    fullWidth
                    onClick={submitHandler}
                    disabled={isSubmitting || (!id && !canCreateUser)}
                    style={{
                      fontWeight: 600,
                      height: "55px",
                      fontSize: "20px",
                      color: "#111214",
                      textTransform: "none",
                      borderRadius: "8px",
                      border: "1px solid #414958",
                      background: isSubmitting ? "#f5f5f5" : "#F9E719",
                      opacity: !id && !canCreateUser ? 0.5 : 1,
                    }}
                  >
                    {isSubmitting && (
                      <CircularProgress size={20} sx={{ color: "#666" }} />
                    )}
                    {isSubmitting ? t("Saving...") : t("Save")}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </div>
        )}
        {!embedded && <Box style={{ height: "30vh" }}></Box>}{" "}
        <Dialog
          open={isResetPasswordModalOpen}
          onClose={handleResetPasswordCancel}
          PaperProps={{
            style: {
              backgroundColor: "#FFFFFF",
              width: isMobile ? "90%" : isTablet ? "80%" : "594px",
              height: "424px",
              borderRadius: "20px",
              maxWidth: "600px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              padding: "40px 80px",
              border: "1px solid #A5DCF3",
              boxShadow: "-2px 2px 5px 0px #00000040",
              gap: "32px",
            },
          }}
          fullWidth
        >
          <DialogTitle
            style={{
              fontFamily: "Roboto",
              fontWeight: "500",
              fontSize: "28px",
              lineHeight: "100%",
              letterSpacing: "0%",
              textAlign: "center",
              color: "#183059",
              padding: "0",
              margin: "0",
            }}
          >
            {t("Reset Password")}
          </DialogTitle>

          <DialogContent
            sx={{
              padding: "0",
              margin: "0",
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <div>
              <h3
                style={{
                  fontFamily: "Roboto",
                  fontWeight: "400",
                  fontSize: "20px",
                  lineHeight: "100%",
                  letterSpacing: "0%",
                  color: "#1F242E",
                  margin: "0 0 12px 0",
                }}
              >
                {t("New Password")} <span style={{ color: "#EE3B2B" }}>*</span>
              </h3>
              <TextField
                className="password-toggle-field"
                fullWidth
                placeholder={t("Enter new password")}
                type={showNewPassword ? "text" : "password"}
                autoComplete="new-password"
                name="newPassword"
                value={pass.newPassword}
                onChange={handlePassChange}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
                sx={{
                  height: "55px",
                  margin: "0",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "16px",
                    border: `2px solid ${passwordError ? "red" : "#183059"}`,
                    padding: "16px 24px",
                    fontSize: "16px",
                    backgroundColor: "#FFFFFF",
                    height: "55px",
                    "& fieldset": {
                      border: "none",
                    },
                    "&:hover fieldset": {
                      border: "none",
                    },
                    "&.Mui-focused fieldset": {
                      border: "none",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "0",
                    height: "auto",
                  },
                }}
              />
            </div>

            <div>
              <h3
                style={{
                  fontFamily: "Roboto",
                  fontWeight: "400",
                  fontSize: "20px",
                  lineHeight: "100%",
                  letterSpacing: "0%",
                  color: "#1F242E",
                  margin: "0 0 12px 0",
                }}
              >
                {t("Confirm New Password")} <span style={{ color: "#EE3B2B" }}>*</span>
              </h3>
              <TextField
                className="password-toggle-field"
                fullWidth
                placeholder={t("Confirm your new password")}
                type={showConfirmPassword ? "text" : "password"}
                autoComplete="new-password"
                name="confirmPassword"
                value={pass.confirmPassword}
                onChange={handlePassChange}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
                sx={{
                  height: "55px",
                  margin: "0",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "16px",
                    border: `2px solid ${passwordError ? "red" : "#183059"}`,
                    padding: "16px 24px",
                    fontSize: "16px",
                    backgroundColor: "#FFFFFF",
                    height: "55px",
                    "& fieldset": {
                      border: "none",
                    },
                    "&:hover fieldset": {
                      border: "none",
                    },
                    "&.Mui-focused fieldset": {
                      border: "none",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "0",
                    height: "auto",
                  },
                }}
              />
            </div>

            {passwordError && (
              <p
                style={{
                  color: "red",
                  margin: "8px 0 0 0",
                  fontFamily: "Roboto",
                  fontSize: "14px",
                }}
              >
                {passwordError}
              </p>
            )}
          </DialogContent>

          <DialogActions
            style={{
              padding: "0",
              gap: "64px",
              justifyContent: "center",
              flexDirection: isMobile ? "column" : "row",
              margin: "0",
            }}
          >
            <button
              onClick={handleResetPasswordCancel}
              className="customButton_add"
              style={{
                width: "122px",
                height: "55px",
                borderRadius: "8px",
                border: "1px solid #414958",
                padding: "16px 24px",
                backgroundColor: "#F2F2F3",
                fontFamily: "Roboto",
                fontSize: isMobile ? "18px" : "24px",
                fontWeight: "600",
                textTransform: "none",
                cursor: "pointer",
                color: "#000000",
              }}
            >
              {t("Cancel")}
            </button>

            <button
              className="customButton_add"
              onClick={handleResetPasswordConfirm}
              style={{
                width: "125px",
                height: "55px",
                borderRadius: "8px",
                border: "1px solid #414958",
                padding: "16px 24px",
                backgroundColor: "#F9E719",
                fontFamily: "Roboto",
                fontSize: isMobile ? "18px" : "24px",
                fontWeight: "600",
                textTransform: "none",
                cursor: "pointer",
                color: "#1F242E",
              }}
            >
              {t("Confirm")}
            </button>
          </DialogActions>
        </Dialog>
      </Box>
      {!embedded && <Footer />}
    </Box>
  );
};

export default AddUser;
